'use client';

import { Fragment, useCallback, useEffect, useId, useRef } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  ATTRIBUTE_LABEL_TEXT,
  ATTRIBUTE_MUTED_INK,
  ATTRIBUTE_MUTED_INK_PLACEHOLDER,
  ATTRIBUTE_VALUE_INK,
  ATTRIBUTE_VALUE_TEXT,
  attributeLabelWidthStyle,
  attributePlaceholder
} from '@/components/attributes';
import {
  ATTRIBUTE_BARE_CONTROL,
  AttributeControlSlot,
  AttributeEditableCell,
  AttributeRow
} from './attribute-row';
import {
  instructionChoiceClass,
  instructionLabelClass,
  instructionPanelClass
} from './instruction-chrome';
import { InstructionChoiceMark } from './instruction-choice-mark';
import { INSTRUCTION_CHOICE_SIZE } from './instruction-chrome';
import { CircleDollarSign, UserCheck, Users } from 'lucide-react';
import type { Contact } from '@/lib/services/contacts/types';
import { getContactDisplayName } from '@/lib/services/contacts/utils';
import type {
  ShareInstruction,
  ShareOwnershipType,
  ShareTransferBeneficiary
} from '@/lib/services/inventory-items/types';
import {
  SHARE_INSTRUCTION_LABELS,
  SHARE_OWNERSHIP_LABELS,
  getAvailableShareInstructions
} from '@/lib/services/inventory-items/types';
import { shareOwnershipChange } from '@/lib/services/inventory-items/share-ownership';
import {
  shareInstructionChange,
  dedupeByContactId
} from '@/lib/services/inventory-items/share-instruction';
import { type ItemBeneficiaryShare } from '@/lib/services/entitlements/specific-gift-defaults';
import { FormField } from './FormField';

// =============================================================================
// Types
// =============================================================================

/**
 * A change to more than one share-instruction field, applied as ONE unit — see
 * {@link ShareInstructionSectionProps.onShareFieldsChange}. A key that is absent
 * is not being changed; `instruction: null` clears it.
 */
export interface ShareInstructionChanges {
  instruction?: ShareInstruction | null;
  ownershipType?: ShareOwnershipType | null;
  /** Only meaningful alongside `ownershipType`, which derives it. */
  isNonEstate?: boolean;
  transferBeneficiaries?: ShareTransferBeneficiary[];
}

interface ShareInstructionSectionProps {
  mode: 'read' | 'edit';
  instruction: ShareInstruction | null | undefined;
  ownershipType: ShareOwnershipType | null | undefined;
  isNonEstate: boolean | undefined;
  transferBeneficiaries: ShareTransferBeneficiary[] | undefined;
  totalUnits: number | undefined;
  contacts?: Contact[];
  onInstructionChange?: (instruction: ShareInstruction | null) => void;
  onOwnershipTypeChange?: (
    ownershipType: ShareOwnershipType | null,
    isNonEstate: boolean
  ) => void;
  onTransferBeneficiariesChange?: (
    beneficiaries: ShareTransferBeneficiary[]
  ) => void;
  /**
   * **Apply several fields as one change (VALE-857).** Two of this section's
   * interactions move more than one `itemInfo` key at once: selecting "Transfer
   * to Beneficiary" also writes the allocation it seeds, and switching ownership
   * to jointly-held also forces the instruction and empties the allocation.
   *
   * A commit-on-blur consumer — the inventory attribute drawer, the only
   * Inventory-tab path since VALE-316 — turns each of the single-field callbacks
   * above into its own `PUT`, with no Save button to coalesce them. Two of those
   * in the same tick raced in the backend's read-modify-write `itemInfo` merge
   * and whichever landed last erased the other's key, so a seeded allocation (or
   * the instruction itself) was silently dropped. **Such a consumer MUST provide
   * this**, and gets one request carrying every changed key.
   *
   * A consumer that batches behind a Save button (`SharesForm`,
   * `HINPortfolioForm`) has nothing to race and can leave it unset: the section
   * then falls back to fanning the same changes out over the single-field
   * callbacks, exactly as before.
   */
  onShareFieldsChange?: (changes: ShareInstructionChanges) => void;
  /** Reports whether the transfer allocation has a validation error */
  onAllocationErrorChange?: (hasError: boolean) => void;
  /**
   * Per-contact specific-gift entitlement shares for this holding (LF-4121).
   * When the lawyer selects "Transfer to Beneficiary" and no beneficiaries are
   * allocated yet, these seed the selection with units allocated by share.
   */
  entitlementBeneficiaryShares?: ItemBeneficiaryShare[];
  /**
   * Whether the data {@link entitlementBeneficiaryShares} and the beneficiary
   * roster are derived from is still in flight (VALE-817).
   *
   * **The seed reads three React Query fetches** — the matter's entitlements,
   * its entitlement classes, and its contacts — and an unloaded one is
   * indistinguishable from a genuine absence: `entitlementBeneficiaryShares` is
   * `undefined` both while the entitlements are loading and when this holding
   * simply has no specific gift, and an empty contact roster drops every share
   * in `buildEntitlementSeed`'s beneficiary filter (`share-instruction.ts`). Without this flag the
   * seed ran against nothing and silently produced an empty panel that reopening
   * the drawer could never re-trigger, because the transition it keys off had
   * already happened.
   *
   * `false`/omitted means "settled" — a caller that supplies shares without it
   * behaves exactly as before.
   */
  entitlementSeedLoading?: boolean;

  /**
   * Which chrome to wear (LF-6314 follow-up). Defaults to `form`, so the shares
   * forms are unchanged.
   *
   * `compact` is the attribute treatment, exactly as `PropertyInstructionSection`'s
   * is: the label/value fields become rows on the shared label column, the choice
   * controls keep their own layout and change only ink and type (the treatment has no
   * arm for a multi-way choice), and the four chrome decisions come from
   * `../instruction-chrome` so the two sections cannot drift apart.
   */
  variant?: 'form' | 'compact';

  /**
   * **The host answers ownership itself, so this section must not** (VALE-863).
   *
   * Tom's 27 Aug review is right that ownership is not an instruction — selling
   * or transferring is a decision about what to *do* with a holding, while
   * sole-versus-joint is a fact about what it *is*. The inventory attribute
   * drawer acts on that: it carries its own `Ownership type` row in `DETAILS`,
   * and both it and the control below write through `shareOwnershipChange`, so
   * the rule has one definition rather than a copy each.
   *
   * It is opt-*out*, per host, and deliberately not derived from `variant`.
   * Removing the control outright took it from every consumer — `SharesForm`,
   * `HINPortfolioForm` and the Redemption share-instructions sheet — none of
   * which grew a replacement row, leaving three surfaces with no way to record
   * ownership at all. Only the drawer has somewhere else to answer it, so only
   * the drawer passes this.
   */
  ownershipAnsweredByHost?: boolean;
}

// =============================================================================
// Instruction icons
// =============================================================================

const INSTRUCTION_ICONS: Record<string, typeof CircleDollarSign> = {
  sell: CircleDollarSign,
  'transfer-to-beneficiary': Users,
  'surviving-joint-holder': UserCheck
};

// =============================================================================
// Helpers
// =============================================================================

/**
 * De-duplicate beneficiary allocations by contactId, keeping the first
 * occurrence. The per-beneficiary rows render the first match per contact, but
 * the allocation total previously summed the raw array — so a duplicated entry
 * (e.g. the same beneficiary appearing twice after an AI/create-matter merge)
 * double-counted the total (3 × 500 displayed as rows, but 3,000 summed instead
 * of 1,500 — LF-4120). Deduping here keeps the total reconciled with the rows
 * and stops a duplicated allocation from being saved.
 */
/**
 * Divide `total` units equally across `count` beneficiaries, distributing any
 * remainder one unit at a time to the leading beneficiaries so the parts sum
 * back to `total` exactly (e.g. 1,800 / 3 -> 600/600/600; 85 / 3 -> 29/28/28).
 * A plain `Math.floor` split would leave the holding under-allocated by the
 * remainder, which the panel then flags as a (spurious) allocation error.
 *
 * Stays here rather than moving with the seed (VALE-1363): this is the MANUAL
 * allocation editor's rule — adding, removing and re-splitting rows by hand —
 * and nothing outside this panel answers that question. The seed's own split is
 * `allocateUnitsByShare`, which is proportional to each entitlement share.
 */
function divideUnitsEqually(total: number, count: number): number[] {
  const base = Math.floor(total / count);
  let remainder = total - base * count;
  return Array.from({ length: count }, () => {
    const extra = remainder > 0 ? 1 : 0;
    if (remainder > 0) remainder -= 1;
    return base + extra;
  });
}

// =============================================================================
// Read Mode
// =============================================================================

function InstructionReadSection({
  instruction,
  ownershipType,
  ownershipAnsweredByHost,
  transferBeneficiaries
}: {
  instruction: ShareInstruction;
  ownershipType: ShareOwnershipType | null | undefined;
  ownershipAnsweredByHost: boolean;
  transferBeneficiaries: ShareTransferBeneficiary[] | undefined;
}) {
  const label = SHARE_INSTRUCTION_LABELS[instruction];
  const ownershipLabel =
    ownershipType && ownershipType !== 'not-held'
      ? SHARE_OWNERSHIP_LABELS[ownershipType]
      : null;

  return (
    <Fragment>
      <div className="border-border-subtle w-full border-t" />
      <div className="flex w-full flex-col gap-2">
        {/*
          **Ownership is answered here unless the host answers it** (VALE-863).
          Tom's 27 Aug review: selling or transferring is a decision about what
          to *do* with a holding, while sole-versus-joint is a fact about what it
          *is*, and this section is the former — so the inventory drawer moved
          the question into its own `DETAILS` row and sets
          `ownershipAnsweredByHost`. Every other consumer still asks it here, and
          would otherwise have nowhere to.
        */}
        {!ownershipAnsweredByHost && !!ownershipLabel && (
          <FormField
            label="Ownership Type"
            value={ownershipLabel}
            mode="read"
          />
        )}
        <FormField label="Instruction" value={label} mode="read" />
        {instruction === 'transfer-to-beneficiary' &&
          !!transferBeneficiaries?.length && (
            <div className="flex min-w-0 items-start gap-2">
              <span className="text-muted-foreground flex-shrink-0 text-sm font-medium">
                Transfer to:
              </span>
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                {transferBeneficiaries.map((tb) => {
                  const name = tb.contact
                    ? getContactDisplayName(tb.contact)
                    : `Contact #${tb.contactId}`;
                  return (
                    <span
                      key={tb.contactId}
                      className="text-muted-foreground text-sm"
                    >
                      {name} ({tb.units.toLocaleString()} units)
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        {instruction === 'surviving-joint-holder' &&
          !!transferBeneficiaries?.length && (
            <FormField
              label="Surviving joint holder"
              value={
                transferBeneficiaries[0].contact
                  ? getContactDisplayName(transferBeneficiaries[0].contact)
                  : `Contact #${transferBeneficiaries[0].contactId}`
              }
              mode="read"
            />
          )}
      </div>
    </Fragment>
  );
}

/**
 * `Instruction`'s value slot: a labelled row in compact, the bare content otherwise.
 *
 * A component rather than a ternary at three call sites because the choice appears
 * three times — pills, and two "nothing available" notes — and all three have to land
 * on the same column or the row's label points at a gap.
 */
function InstructionSlot({
  compact,
  children
}: {
  compact: boolean;
  children: React.ReactNode;
}) {
  if (!compact) return <>{children}</>;
  return (
    <AttributeRow label="Instruction">
      <AttributeControlSlot className="flex-wrap">
        {children}
      </AttributeControlSlot>
    </AttributeRow>
  );
}

// =============================================================================
// Edit Mode
// =============================================================================

function InstructionEditSection({
  instruction,
  ownershipType,
  isNonEstate,
  transferBeneficiaries,
  totalUnits,
  contacts,
  onInstructionChange,
  onOwnershipTypeChange,
  onTransferBeneficiariesChange,
  onShareFieldsChange,
  onAllocationErrorChange,
  entitlementBeneficiaryShares,
  entitlementSeedLoading = false,
  variant = 'form',
  ownershipAnsweredByHost = false
}: Omit<ShareInstructionSectionProps, 'mode'>) {
  const compact = variant === 'compact';
  const resolvedIsNonEstate =
    ownershipType === 'joint-tenants' ? true : (isNonEstate ?? false);
  const availableInstructions =
    getAvailableShareInstructions(resolvedIsNonEstate);
  const isLockedToSjh = ownershipType === 'joint-tenants';

  // Auto-set SJH for jointly held shares
  useEffect(() => {
    if (isLockedToSjh && instruction !== 'surviving-joint-holder') {
      onInstructionChange?.('surviving-joint-holder');
    }
  }, [isLockedToSjh, instruction, onInstructionChange]);

  // Report allocation validation state to parent. Only a *partial* allocation
  // blocks save: with no beneficiaries selected the lawyer is deferring the
  // allocation ("you can Transfer to Beneficiary at a later time"), which is a
  // valid, saveable state. We only flag an error once they've started
  // allocating but the units don't reconcile to the total. (LF-4068)
  const uniqueAllocations = dedupeByContactId(transferBeneficiaries ?? []);
  const allocatedTotal = uniqueAllocations.reduce(
    (sum, tb) => sum + tb.units,
    0
  );
  const hasAllocationError =
    instruction === 'transfer-to-beneficiary' &&
    !!totalUnits &&
    uniqueAllocations.length > 0 &&
    allocatedTotal !== totalUnits;

  useEffect(() => {
    onAllocationErrorChange?.(hasAllocationError);
  }, [hasAllocationError, onAllocationErrorChange]);

  const beneficiaries = (contacts ?? []).filter((c) => c.isBeneficiary);

  // Seed transfer beneficiaries from the matter's specific-gift entitlements the
  // first time the lawyer selects "Transfer to Beneficiary" with nothing yet
  // allocated (LF-4121). We seed ONLY on this transition — never on reopen — so a
  // deliberately-deferred empty allocation (LF-4068) is preserved.
  //
  // Applying it is factored out because the transition is not the only moment it
  // can run — see `pendingSeedRef` below. Both guards live here so the deferred
  // path cannot drift from the immediate one: never against a panel that already
  // has an allocation, and never when this holding has no specific gift.
  /*
    **The guards live in `shareInstructionChange` now** (VALE-1363), because the
    AI-extract review answers the instruction as a `select` row in its detail
    block and has to seed identically. Asking the rule for the whole change and
    reading its allocation back is what keeps ONE definition: the deferred path
    below needs the allocation on its own, and re-deriving it here is exactly
    the second copy the extraction removes.
  */
  const buildSeedIfApplicable = useCallback(
    (): ShareTransferBeneficiary[] | null =>
      shareInstructionChange('transfer-to-beneficiary', {
        transferBeneficiaries,
        entitlementBeneficiaryShares,
        totalUnits,
        contacts
      }).transferBeneficiaries ?? null,
    [transferBeneficiaries, entitlementBeneficiaryShares, totalUnits, contacts]
  );

  /*
    **The transition can outrun the data the seed reads** (VALE-817). Click the
    pill before the entitlement / contact fetches land and the seed built nothing
    — and there was no second chance, because the one transition it keys off had
    already been spent: reopening the drawer does not re-trigger it (by design,
    LF-4068), so the lawyer re-entered every beneficiary and unit count by hand
    with nothing on screen saying the automation had no-opped.

    So a transition that finds those sources still loading *arms* the seed rather
    than dropping it, and the effect below applies it the moment they settle.
    Armed only on the transition, fired at most once, and only into a still-empty
    panel — which is what keeps all three of LF-4068's guarantees intact: no seed
    on reopen, no clobbering an edit, and a deliberately-emptied allocation stays
    empty.
  */
  const pendingSeedRef = useRef(false);

  /*
    **Every multi-key change goes through here** (VALE-857) — see
    `onShareFieldsChange`'s doc comment for why one request beats several. The
    fallback is the pre-existing fan-out, kept for the Save-button forms, where
    each callback sets a react-hook-form field and nothing reaches the server
    until Save. Its order is the one those handlers already used.
  */
  const commitShareFields = useCallback(
    (changes: ShareInstructionChanges) => {
      if (onShareFieldsChange) {
        onShareFieldsChange(changes);
        return;
      }
      if (changes.ownershipType !== undefined) {
        onOwnershipTypeChange?.(
          changes.ownershipType,
          changes.isNonEstate ?? false
        );
      }
      if (changes.instruction !== undefined) {
        onInstructionChange?.(changes.instruction);
      }
      if (changes.transferBeneficiaries !== undefined) {
        onTransferBeneficiariesChange?.(changes.transferBeneficiaries);
      }
    },
    [
      onShareFieldsChange,
      onOwnershipTypeChange,
      onInstructionChange,
      onTransferBeneficiariesChange
    ]
  );

  /*
    **One rule, two answers** (VALE-863). `shareOwnershipChange` owns what an
    ownership answer writes — `joint-tenants` forcing `surviving-joint-holder`
    and the non-estate flag, `solely-held` undoing it. The drawer's `DETAILS`
    row calls the same function from its `toPatch`, so the two ways of answering
    cannot drift; this handler used to *be* that rule, which is exactly what
    made it unsafe to answer anywhere else.
  */
  const handleOwnershipChange = (value: string) => {
    commitShareFields(
      shareOwnershipChange(value as ShareOwnershipType, instruction)
    );
  };

  const ownershipSelect = (close?: () => void) => (
    <Select
      value={ownershipType ?? ''}
      onValueChange={handleOwnershipChange}
      /*
        In compact the row opens *into* this select, so its list should already be
        showing — and closing the list closes the row, because a portalled list leaves
        the cell's own blur check nothing to see.
      */
      defaultOpen={compact || undefined}
      onOpenChange={
        close
          ? (listOpen) => {
              if (!listOpen) close();
            }
          : undefined
      }
    >
      <SelectTrigger
        className={cn(compact ? `${ATTRIBUTE_BARE_CONTROL} w-full` : 'h-8')}
        placeholderColor={compact ? ATTRIBUTE_MUTED_INK_PLACEHOLDER : undefined}
      >
        <SelectValue
          placeholder={
            compact
              ? attributePlaceholder('Ownership type')
              : 'Select ownership type'
          }
        />
      </SelectTrigger>
      <SelectContent>
        {Object.entries(SHARE_OWNERSHIP_LABELS).map(([type, label]) => (
          <SelectItem key={type} value={type}>
            {label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const handleInstructionSelect = (
    inst: ShareInstruction,
    isActive: boolean
  ) => {
    const next = isActive ? null : inst;
    /*
      **Arming outranks the rule, and only here.** A transition into
      `transfer-to-beneficiary` whose entitlement data is still in flight has no
      seed to compute yet, so it arms the effect below instead (VALE-817) — that
      is genuinely stateful and stays with the component. Everything else about
      what an instruction answer writes is `shareInstructionChange`'s
      (VALE-1363), including the three seed guards.
    */
    if (next === 'transfer-to-beneficiary' && entitlementSeedLoading) {
      pendingSeedRef.current = true;
      commitShareFields({ instruction: next });
      return;
    }
    if (next !== 'transfer-to-beneficiary') {
      // Leaving the instruction spends the pending seed — it belonged to the
      // transition the lawyer has just undone.
      pendingSeedRef.current = false;
    }
    // The instruction and the allocation it seeds are ONE change — they were two
    // un-awaited commits, and in the drawer that is two racing PUTs (VALE-857).
    commitShareFields(
      shareInstructionChange(next, {
        transferBeneficiaries,
        entitlementBeneficiaryShares,
        totalUnits,
        contacts
      })
    );
  };

  useEffect(() => {
    if (!pendingSeedRef.current || entitlementSeedLoading) return;
    // Settled: this is the armed transition's one shot, spent either way.
    pendingSeedRef.current = false;
    if (instruction !== 'transfer-to-beneficiary') return;
    // The deferred path has no instruction change to carry — the transition it
    // belongs to committed one tick ago — so this is a genuine single-key write.
    const seed = buildSeedIfApplicable();
    if (seed) commitShareFields({ transferBeneficiaries: seed });
  }, [
    entitlementSeedLoading,
    instruction,
    buildSeedIfApplicable,
    commitShareFields
  ]);

  return (
    <Fragment>
      {/*
        The rule above the block belongs to the *form* chrome. On the attribute
        treatment a section is already ruled by its own group heading, so a second
        hairline here draws a boundary inside one section (§1.10).
      */}
      {!compact && <div className="border-border w-full border-t" />}
      <div
        className={cn(
          'flex w-full flex-col',
          compact ? '@container gap-2' : 'gap-3'
        )}
        /*
          The label column, published here so this block's rows land on the same
          column as the fields above it — an undefined `--attr-label-w` makes
          `ATTRIBUTE_LABEL`'s width invalid and each label falls back to its own text.
        */
        style={compact ? attributeLabelWidthStyle(2) : undefined}
      >
        {/*
          Instruction pills. In compact the label takes the row's own column rather
          than stacking above a full-width band — an isolated review read the stacked
          version as "flush with the label column, so the row reads as a full-width
          band rather than a label/value pair".
        */}
        {/*
          Ownership, unless the host answers it — see `ownershipAnsweredByHost`.
          The inventory drawer does; `SharesForm`, `HINPortfolioForm` and the
          Redemption sheet do not, and this is their only ownership control.
        */}
        {!ownershipAnsweredByHost &&
          (compact ? (
            <AttributeRow label="Ownership type">
              <AttributeEditableCell
                display={
                  ownershipType && ownershipType !== 'not-held'
                    ? (SHARE_OWNERSHIP_LABELS[ownershipType] ?? '')
                    : ''
                }
                placeholder={attributePlaceholder('Ownership type')}
                // Portalled list — the select closes the cell, not blur.
                closeOnBlur={false}
                renderEditor={(close) => ownershipSelect(close)}
              />
            </AttributeRow>
          ) : (
            <div className="flex flex-col gap-1.5">
              <Label className="text-sm font-medium">Ownership Type</Label>
              {ownershipSelect()}
            </div>
          ))}

        {!compact && <Label className="text-sm font-medium">Instruction</Label>}

        {/*
          The ownership gate holds only while nothing is recorded (VALE-1940). An
          instruction the AI proposed, or one stored before ownership was
          answered, has to stay visible and correctable rather than hide behind
          the prompt to set ownership.
        */}
        {!ownershipType && !instruction ? (
          <InstructionSlot compact={compact}>
            <p
              className={cn(
                'italic',
                compact
                  ? `${ATTRIBUTE_LABEL_TEXT} ${ATTRIBUTE_MUTED_INK}`
                  : 'text-muted-foreground text-xs'
              )}
            >
              Set ownership type to see available instructions
            </p>
          </InstructionSlot>
        ) : availableInstructions.length === 0 ? (
          <InstructionSlot compact={compact}>
            <p
              className={cn(
                'italic',
                compact
                  ? `${ATTRIBUTE_LABEL_TEXT} ${ATTRIBUTE_MUTED_INK}`
                  : 'text-muted-foreground text-xs'
              )}
            >
              No instructions available for this ownership type
            </p>
          </InstructionSlot>
        ) : (
          <InstructionSlot compact={compact}>
            <div className="flex flex-wrap gap-1.5">
              {availableInstructions.map((inst) => {
                const isActive = instruction === inst;
                const Icon = INSTRUCTION_ICONS[inst] ?? CircleDollarSign;

                return (
                  <button
                    key={inst}
                    type="button"
                    disabled={isLockedToSjh}
                    onClick={() => handleInstructionSelect(inst, isActive)}
                    className={cn(
                      'inline-flex cursor-pointer items-center gap-1.5 border font-medium transition-all',
                      compact
                        ? INSTRUCTION_CHOICE_SIZE
                        : 'rounded-full px-3 py-1.5 text-xs',
                      instructionChoiceClass(compact, isActive),
                      isLockedToSjh && 'cursor-default'
                    )}
                  >
                    <Icon
                      className={cn(
                        'h-3.5 w-3.5',
                        // THE INK HAS TO FOLLOW ITS OWN CHIP'S FILL, and the two
                        // variants have different fills — see
                        // `instructionChoiceClass`: "filled near-black in form
                        // chrome, tinted in compact".
                        //
                        //   !compact + active -> `bg-foreground` (a filled pill)
                        //                        so the ink is `text-background`
                        //   compact  + active -> `bg-surface-subtle` (a tint)
                        //                        so the ink is `text-foreground`
                        //
                        // The `!compact` arm was `text-white`, which is correct
                        // in LIGHT (white on near-black) and inverts wrongly in
                        // DARK, where `--foreground` is near-white and the glyph
                        // would vanish into its own fill. `--background` is the
                        // pairing the mapping table specifies for a
                        // `bg-foreground` surface and it flips with it, so light
                        // is preserved (Δ3) and dark is fixed — the same
                        // substitution this tranche made at seven other
                        // `text-white`-on-a-disc sites (VALE-529).
                        isActive
                          ? compact
                            ? 'text-foreground'
                            : 'text-background'
                          : 'text-foreground-subtle'
                      )}
                    />
                    {SHARE_INSTRUCTION_LABELS[inst]}
                    <InstructionChoiceMark
                      compact={compact}
                      isActive={isActive}
                    />
                  </button>
                );
              })}
            </div>
          </InstructionSlot>
        )}

        {/* Beneficiary allocation panel for Transfer */}
        {instruction === 'transfer-to-beneficiary' && (
          <BeneficiaryAllocationPanel
            compact={compact}
            beneficiaries={beneficiaries}
            transferBeneficiaries={transferBeneficiaries ?? []}
            totalUnits={totalUnits ?? 0}
            onChange={onTransferBeneficiariesChange}
          />
        )}

        {/* Surviving joint holder contact picker */}
        {instruction === 'surviving-joint-holder' && (
          <SurvivingJointHolderPicker
            compact={compact}
            contacts={contacts ?? []}
            selectedContactId={transferBeneficiaries?.[0]?.contactId ?? null}
            totalUnits={totalUnits ?? 0}
            onChange={onTransferBeneficiariesChange}
          />
        )}
      </div>
    </Fragment>
  );
}

// =============================================================================
// Beneficiary Allocation Panel
// =============================================================================

function SurvivingJointHolderPicker({
  compact = false,
  contacts,
  selectedContactId,
  totalUnits,
  onChange
}: {
  compact?: boolean;
  contacts: Contact[];
  selectedContactId: number | null;
  totalUnits: number;
  onChange?: (beneficiaries: ShareTransferBeneficiary[]) => void;
}) {
  const handleChange = (value: string) => {
    if (!value) {
      onChange?.([]);
      return;
    }
    const contactId = parseInt(value, 10);
    const contact = contacts.find((c) => c.id === contactId);
    onChange?.([
      {
        contactId,
        contact: contact
          ? {
              id: contact.id,
              firstName: contact.firstName ?? '',
              lastName: contact.lastName ?? '',
              organisationName: contact.organisationName ?? null
            }
          : undefined,
        units: totalUnits
      }
    ]);
  };

  return (
    <div className="flex flex-col gap-1.5">
      <Label
        className={cn(
          compact
            ? instructionLabelClass(true)
            : 'text-foreground-secondary text-xs font-medium'
        )}
      >
        Surviving joint holder
      </Label>
      <Select
        value={selectedContactId?.toString() ?? ''}
        onValueChange={handleChange}
      >
        <SelectTrigger
          className={cn(compact ? `${ATTRIBUTE_BARE_CONTROL} w-full` : 'h-8')}
          placeholderColor={
            compact ? ATTRIBUTE_MUTED_INK_PLACEHOLDER : undefined
          }
        >
          <SelectValue placeholder="Select contact" />
        </SelectTrigger>
        <SelectContent>
          {contacts.map((c) => (
            <SelectItem key={c.id} value={c.id.toString()}>
              {getContactDisplayName(c)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function BeneficiaryAllocationPanel({
  compact = false,
  beneficiaries,
  transferBeneficiaries,
  totalUnits,
  onChange
}: {
  compact?: boolean;
  beneficiaries: Contact[];
  transferBeneficiaries: ShareTransferBeneficiary[];
  totalUnits: number;
  onChange?: (beneficiaries: ShareTransferBeneficiary[]) => void;
}) {
  const panelId = useId();
  // Work from a contact-deduped list so the rendered rows, the allocation total,
  // and any change emitted upward all agree — and a duplicated entry is never
  // re-saved (LF-4120).
  const uniqueBeneficiaries = dedupeByContactId(transferBeneficiaries);
  const handleToggle = (contact: Contact) => {
    const existing = uniqueBeneficiaries.find(
      (tb) => tb.contactId === contact.id
    );
    if (existing) {
      const remaining = uniqueBeneficiaries.filter(
        (tb) => tb.contactId !== contact.id
      );
      if (remaining.length > 0) {
        const divided = divideUnitsEqually(totalUnits, remaining.length);
        onChange?.(remaining.map((tb, i) => ({ ...tb, units: divided[i] })));
      } else {
        onChange?.([]);
      }
    } else {
      const newList = [
        ...uniqueBeneficiaries,
        {
          contactId: contact.id,
          contact: {
            id: contact.id,
            firstName: contact.firstName ?? '',
            lastName: contact.lastName ?? '',
            organisationName: contact.organisationName ?? null
          },
          units: 0
        }
      ];
      const divided = divideUnitsEqually(totalUnits, newList.length);
      onChange?.(newList.map((tb, i) => ({ ...tb, units: divided[i] })));
    }
  };

  const handleUnitsChange = (contactId: number, units: number) => {
    onChange?.(
      uniqueBeneficiaries.map((tb) =>
        tb.contactId === contactId ? { ...tb, units } : tb
      )
    );
  };

  // Auto-divide units equally across the selected beneficiaries when the
  // holding has beneficiaries selected but nothing is allocated yet — the
  // state the lawyer lands on when a holding was seeded (entitlements /
  // create-matter) with beneficiaries at zero units. Matches the expected
  // behaviour: 1,800 across 3 → 600 each, still editable afterward (LF-4335).
  // Fires at most once per mount (a ref guard), and ONLY from the
  // fully-unallocated state, so it never clobbers a deliberate split nor fights
  // a lawyer clearing fields to re-enter them. Selecting beneficiaries via the
  // checkboxes already divides in handleToggle; this covers the seeded/persisted
  // case where that path was never taken.
  const didAutoDivideRef = useRef(false);
  useEffect(() => {
    if (didAutoDivideRef.current) return;
    const unique = dedupeByContactId(transferBeneficiaries);
    if (unique.length === 0 || totalUnits <= 0) return;
    const allocated = unique.reduce((sum, tb) => sum + tb.units, 0);
    didAutoDivideRef.current = true;
    if (allocated !== 0) return;
    const divided = divideUnitsEqually(totalUnits, unique.length);
    onChange?.(unique.map((tb, i) => ({ ...tb, units: divided[i] })));
  }, [transferBeneficiaries, totalUnits, onChange]);

  if (beneficiaries.length === 0) {
    return (
      <p className="text-muted-foreground text-xs italic">
        No beneficiaries found on this matter. Add contacts with the beneficiary
        role first.
      </p>
    );
  }

  const allocatedUnits = uniqueBeneficiaries.reduce(
    (sum, tb) => sum + tb.units,
    0
  );
  const allocationIsExact = allocatedUnits === totalUnits;

  return (
    <div
      className={cn(
        'flex flex-col gap-2 p-3',
        compact
          ? instructionPanelClass(true)
          : 'border-border bg-surface-subtle/50 rounded-lg border'
      )}
    >
      <Label
        className={cn(
          compact
            ? instructionLabelClass(true)
            : 'text-foreground-secondary text-xs font-medium'
        )}
      >
        Transfer to beneficiaries
      </Label>
      {beneficiaries.map((ben) => {
        const allocation = uniqueBeneficiaries.find(
          (tb) => tb.contactId === ben.id
        );
        const isSelected = !!allocation;
        const name = getContactDisplayName(ben);

        return (
          <div
            key={ben.id}
            className={cn(
              'flex items-center gap-2 rounded-md transition-colors',
              compact
                ? /*
                    **No border in compact.** An isolated review of the *property*
                    section's identical rows measured "~760px of container for ~90px of
                    text" and read the bordered box as a heavier language than the rows
                    around it. The row keeps its full width here — unlike property's
                    chips — because a selected beneficiary carries a units input on the
                    right, so the width is doing work rather than sitting empty.
                  */
                  cn(
                    'hover:bg-surface-subtle px-1.5 py-1',
                    isSelected && 'bg-surface-subtle'
                  )
                : cn(
                    'border px-2.5 py-1.5',
                    isSelected
                      ? 'border-border-strong bg-card'
                      : 'border-border-subtle bg-card/50'
                  )
            )}
          >
            {/*
              **The tick stays a tick here, and the row becomes the target.** The
              segmented `Yes` / `No` that replaced the value-confirmation and
              non-estate booleans is a *binary* control; choosing which of N
              beneficiaries receive units is a multi-select, and each row also carries
              a units input — so a chip row (the property section's answer) has
              nowhere to put it. What compact changes is the size, because the default
              lands at 13.6px under this theme's `--spacing` and reads as weak against
              the name, and the row's own ink.
            */}
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => handleToggle(ben)}
              id={`${panelId}-ben-${ben.id}`}
            />
            <Label
              htmlFor={`${panelId}-ben-${ben.id}`}
              className={cn(
                'flex-1 cursor-pointer font-medium',
                compact
                  ? `${ATTRIBUTE_VALUE_TEXT} ${ATTRIBUTE_VALUE_INK}`
                  : 'text-foreground text-xs'
              )}
            >
              {name}
            </Label>
            {isSelected && (
              <div className="flex items-center gap-1">
                <Input
                  type="number"
                  min={0}
                  max={totalUnits}
                  value={allocation?.units || ''}
                  onChange={(e) =>
                    handleUnitsChange(ben.id, parseInt(e.target.value) || 0)
                  }
                  onFocus={(e) => e.target.select()}
                  className="border-border bg-card h-7 w-20 border px-2 text-right text-xs"
                  placeholder="units"
                />
                <span className="text-foreground-subtle text-xs">units</span>
              </div>
            )}
          </div>
        );
      })}
      {transferBeneficiaries.length > 0 && (
        <div className="flex flex-col items-end gap-0.5">
          <span
            className={cn(
              'text-xs',
              allocationIsExact
                ? 'text-foreground-subtle'
                : 'text-error font-medium'
            )}
          >
            {allocatedUnits.toLocaleString()} / {totalUnits.toLocaleString()}{' '}
            units allocated
          </span>
          {!allocationIsExact && (
            <span className="text-error text-xs">
              {allocatedUnits < totalUnits
                ? `${(totalUnits - allocatedUnits).toLocaleString()} units under-allocated`
                : `${(allocatedUnits - totalUnits).toLocaleString()} units over-allocated`}
            </span>
          )}
          {allocatedUnits < totalUnits && (
            <span className="text-muted-foreground text-xs">
              If allocations are still unknown, you can &ldquo;Transfer to
              Beneficiary&rdquo; at a later time.
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function ShareInstructionSection({
  mode,
  instruction,
  ownershipType,
  isNonEstate,
  transferBeneficiaries,
  totalUnits,
  contacts,
  onInstructionChange,
  onOwnershipTypeChange,
  onTransferBeneficiariesChange,
  onShareFieldsChange,
  onAllocationErrorChange,
  entitlementBeneficiaryShares,
  entitlementSeedLoading,
  variant = 'form',
  ownershipAnsweredByHost = false
}: ShareInstructionSectionProps) {
  if (mode === 'read') {
    if (!instruction || instruction === 'undecided') {
      return (
        <Fragment>
          <div className="border-border-subtle w-full border-t" />
          <div className="flex w-full flex-col gap-2">
            {!ownershipAnsweredByHost && (
              <FormField
                label="Ownership Type"
                value={
                  ownershipType && ownershipType !== 'not-held'
                    ? SHARE_OWNERSHIP_LABELS[ownershipType]
                    : '—'
                }
                mode="read"
              />
            )}
            <FormField label="Instruction" value="—" mode="read" />
          </div>
        </Fragment>
      );
    }
    return (
      <InstructionReadSection
        instruction={instruction}
        ownershipType={ownershipType}
        ownershipAnsweredByHost={ownershipAnsweredByHost}
        transferBeneficiaries={transferBeneficiaries}
      />
    );
  }

  return (
    <InstructionEditSection
      variant={variant}
      instruction={instruction}
      ownershipType={ownershipType}
      isNonEstate={isNonEstate}
      transferBeneficiaries={transferBeneficiaries}
      totalUnits={totalUnits}
      contacts={contacts}
      onInstructionChange={onInstructionChange}
      onOwnershipTypeChange={onOwnershipTypeChange}
      onTransferBeneficiariesChange={onTransferBeneficiariesChange}
      onShareFieldsChange={onShareFieldsChange}
      onAllocationErrorChange={onAllocationErrorChange}
      entitlementBeneficiaryShares={entitlementBeneficiaryShares}
      entitlementSeedLoading={entitlementSeedLoading}
      ownershipAnsweredByHost={ownershipAnsweredByHost}
    />
  );
}
